import React, { useState } from 'react';
import { Eye, EyeOff, User } from 'lucide-react';
import { COUNTRY_CODES } from '../../../lib/onboarding/avatarConfig';
import { formatPhoneNumber, validateEmail, validatePassword } from '../../../utils/validation';
import type { UnifiedOnboardingData } from '../../../lib/onboarding/unifiedTypes';

interface AccountCreationStepProps {
  data: UnifiedOnboardingData;
  onUpdate: (updates: Partial<UnifiedOnboardingData>) => void;
  onNext: () => void;
}

export default function AccountCreationStep({ data, onUpdate, onNext }: AccountCreationStepProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleEmailChange = (email: string) => {
    onUpdate({ email });
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handleFullNameChange = (fullName: string) => {
    onUpdate({ fullName });
    if (errors.fullName) {
      setErrors(prev => ({ ...prev, fullName: '' }));
    }
  };

  const handlePasswordChange = (password: string) => {
    onUpdate({ password });
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handlePhoneChange = (phoneNumber: string) => {
    onUpdate({ phoneNumber: formatPhoneNumber(phoneNumber) });
    if (errors.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!validateEmail(data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (data.fullName.trim().length < 2) {
      newErrors.fullName = 'Please enter your full name (at least 2 characters)';
    }

    const passwordValidation = validatePassword(data.password);
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0];
    }

    if (!data.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <User className="h-12 w-12 text-pink-400 hidden md:block mx-auto mb-4" />
        <h3 className="md:text-lg font-semibold text-gray-900 mb-2">
          Create your account
        </h3>
        <p className="md:text-base text-sm text-gray-600">
          We'll send you daily prompts via WhatsApp
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <input
            id="fullName"
            type="text"
            value={data.fullName}
            onChange={(e) => handleFullNameChange(e.target.value)}
            placeholder="Enter your full name"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
            aria-describedby={errors.fullName ? "fullName-error" : undefined}
            aria-invalid={!!errors.fullName}
          />
          {errors.fullName && (
            <p id="fullName-error" className="text-red-500 text-sm mt-1" role="alert">
              {errors.fullName}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="flex space-x-2">
            <select
              value={data.countryCode}
              onChange={(e) => onUpdate({ countryCode: e.target.value })}
              className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
              aria-label="Country code"
            >
              {COUNTRY_CODES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.code}
                </option>
              ))}
            </select>
            <input
              id="phone"
              type="tel"
              value={data.phoneNumber}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="123-456-7890"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
              aria-describedby={errors.phoneNumber ? "phone-error" : undefined}
              aria-invalid={!!errors.phoneNumber}
            />
          </div>
          {errors.phoneNumber && (
            <p id="phone-error" className="text-red-500 text-sm mt-1" role="alert">
              {errors.phoneNumber}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={data.email}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
            aria-describedby={errors.email ? "email-error" : undefined}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p id="email-error" className="text-red-500 text-sm mt-1" role="alert">
              {errors.email}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={data.password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              placeholder="Create a secure password"
              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
              aria-describedby={errors.password ? "password-error" : "password-help"}
              aria-invalid={!!errors.password}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 rounded"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          {errors.password ? (
            <p id="password-error" className="text-red-500 text-sm mt-1" role="alert">
              {errors.password}
            </p>
          ) : (
            <p id="password-help" className="text-gray-500 text-sm mt-1">
              Must be at least 8 characters with uppercase, lowercase, and number
            </p>
          )}
        </div>
      </form>
    </div>
  );
}